import { useEffect, useState } from 'react'
import { SITE } from '@/lib/site'
import type { Announcement } from '@/lib/wp'

const DISMISS_KEY = 'mh-announce-dismissed'

/**
 * Slim bar pinned above the nav. The content is baked into the static HTML at
 * build time (`initial`) so it paints immediately (no late LCP), then refreshed
 * on the client for freshness. Dismissible; dismissal is remembered per session.
 */
export function AnnouncementBar({ initial = null }: { initial?: Announcement | null }) {
  const [data, setData] = useState<Announcement | null>(initial)
  const [dismissed, setDismissed] = useState(false)

  useEffect(() => {
    try {
      if (sessionStorage.getItem(DISMISS_KEY) === '1') setDismissed(true)
    } catch {
      /* ignore */
    }
    const ctrl = new AbortController()
    fetch(`${SITE.wpApi}/mitohacker/v1/announcement`, { signal: ctrl.signal })
      .then((r) => (r.ok ? r.json() : null))
      .then((json: Announcement | null) => {
        setData(json && json.enabled && json.text ? json : null)
      })
      .catch(() => {})
    return () => ctrl.abort()
  }, [])

  if (!data || dismissed) return null

  const accent = data.accent || 'var(--color-energy)'

  return (
    <div
      className="relative z-50 border-b border-line/70 bg-surface/95 backdrop-blur"
      style={{ ['--bar-accent' as string]: accent }}
    >
      <div className="container-page flex items-center justify-center gap-3 py-2 text-center text-[13px]">
        <span
          aria-hidden
          className="hidden h-1.5 w-1.5 shrink-0 rounded-full sm:block"
          style={{ background: 'var(--bar-accent)', boxShadow: '0 0 10px var(--bar-accent)' }}
        />
        <a href={data.link || '#'} className="truncate font-medium text-mist transition-colors hover:text-energy">
          {data.text}
        </a>
        <button
          type="button"
          aria-label="Dismiss announcement"
          onClick={() => {
            try {
              sessionStorage.setItem(DISMISS_KEY, '1')
            } catch {
              /* ignore */
            }
            setDismissed(true)
          }}
          className="absolute right-4 grid h-6 w-6 place-items-center rounded-full text-fog transition-colors hover:bg-line hover:text-mist"
        >
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none" aria-hidden>
            <path d="M1 1l10 10M11 1L1 11" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
          </svg>
        </button>
      </div>
    </div>
  )
}
